import React from 'react';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

export const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="mb-4 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          
          <div className="mb-6">
            <h2 className="text-3xl font-light tracking-wide">
              <span className="text-slate-900">eco</span>
              <span className="text-green-500 font-medium">logical</span>
            </h2>
          </div>
          
          <h1 className="text-3xl font-light text-slate-900">Terms of Service</h1>
          <p className="text-slate-600 mt-2">Last updated: January 2024</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          
          <h2 className="text-2xl font-medium text-slate-900 mb-4">1. Agreement to Terms</h2>
          <p className="text-slate-600 mb-6">
            By accessing and using the services provided by Ecological Energy Ltd ("Company", "we", "us"), 
            you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, 
            please do not use our services.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">2. Company Information</h2>
          <p className="text-slate-600 mb-6">
            <strong>Ecological Energy Ltd</strong><br />
            Registered in England and Wales<br />
            Company Registration Number: [Your company number]<br />
            Registered Office: [Your registered office address]<br />
            Email: <a href="mailto:info@ecological.energy" className="text-slate-600 hover:text-slate-800 no-underline">info@ecological.energy</a>
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">3. Services Description</h2>
          <p className="text-slate-600 mb-6">
            We provide renewable energy development consultancy services, including but not limited to:
            planning and permissions assistance, grid connection services, project financing advice, 
            onshore wind development, commercial solar solutions, and energy storage consultation.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">4. Professional Services</h2>
          <p className="text-slate-600 mb-6">
            Our consultancy services are provided by qualified professionals. However, you acknowledge that:
            <br />• All advice is given in good faith based on current information
            <br />• External factors may affect project outcomes
            <br />• Regulatory and planning permissions are subject to third-party approval
            <br />• We recommend seeking independent legal and financial advice
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">5. Client Obligations</h2>
          <p className="text-slate-600 mb-4">As our client, you agree to:</p>
          <ul className="list-disc pl-6 text-slate-600 mb-6">
            <li>Provide accurate and complete information</li>
            <li>Cooperate with our reasonable requests</li>
            <li>Pay fees according to agreed terms</li>
            <li>Comply with all applicable laws and regulations</li>
            <li>Maintain confidentiality of proprietary information</li>
          </ul>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">6. Fees and Payment</h2>
          <p className="text-slate-600 mb-6">
            Fees for services will be agreed upon in writing before commencement. Payment terms are typically 
            30 days from invoice date unless otherwise agreed. Late payment may incur interest charges at 8% 
            above the Bank of England base rate per annum.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">7. Intellectual Property</h2>
          <p className="text-slate-600 mb-6">
            All materials, reports, and advice provided remain our intellectual property unless specifically 
            transferred in writing. You may use our deliverables for your intended project but may not 
            reproduce or distribute them without consent.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">8. Confidentiality</h2>
          <p className="text-slate-600 mb-6">
            We maintain strict confidentiality regarding all client information and projects. We expect 
            similar discretion regarding our methods, processes, and proprietary information.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">9. Limitation of Liability</h2>
          <p className="text-slate-600 mb-6">
            To the fullest extent permitted by law, our liability is limited to the fees paid for the 
            specific service in question. We exclude liability for indirect, consequential, or special 
            damages including lost profits, unless caused by our negligence or wilful misconduct.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">10. Insurance</h2>
          <p className="text-slate-600 mb-6">
            We maintain appropriate professional indemnity insurance. Details of our insurance coverage 
            are available upon request.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">11. Termination</h2>
          <p className="text-slate-600 mb-6">
            Either party may terminate services with reasonable notice. Upon termination, you remain liable 
            for fees incurred up to the termination date. We will return client materials and maintain 
            confidentiality obligations.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">12. Force Majeure</h2>
          <p className="text-slate-600 mb-6">
            Neither party shall be liable for delays or failures due to circumstances beyond reasonable control, 
            including but not limited to natural disasters, government actions, or pandemic restrictions.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">13. Dispute Resolution</h2>
          <p className="text-slate-600 mb-6">
            Any disputes will first be addressed through good faith negotiations. Unresolved disputes shall 
            be subject to the exclusive jurisdiction of the English courts and governed by English law.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">14. Website Use</h2>
          <p className="text-slate-600 mb-6">
            Our website is provided for informational purposes. While we strive for accuracy, we do not 
            warrant that all information is current or complete. Use of our website constitutes acceptance 
            of these terms.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">15. Data Protection</h2>
          <p className="text-slate-600 mb-6">
            We process personal data in accordance with our Privacy Policy and UK data protection laws. 
            By using our services, you consent to such processing for the purposes outlined.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">16. Amendments</h2>
          <p className="text-slate-600 mb-6">
            We may update these Terms from time to time. Continued use of our services after changes 
            constitutes acceptance of updated Terms. Significant changes will be communicated to existing clients.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">17. Contact Information</h2>
          <p className="text-slate-600 mb-6">
            For questions about these Terms of Service, please contact us:
            <br /><br />
            <strong>Email:</strong> <a href="mailto:info@ecological.energy" className="text-slate-600 hover:text-slate-800 no-underline">info@ecological.energy</a><br />
            <strong>Phone:</strong> +44(0)1392 249230<br />
            <strong>Address:</strong> [Your registered office address]
          </p>

        </div>
      </div>
    </div>
  );
};